// Volume threshold above which a task must route through the Compliance
// stage after BizFin approval. Client-side constant while the BE contract
// hasn't yet added it to task-type config — swap to a schema-sourced value
// as soon as the field is available. Shared between BizFinancePanel (drives
// bizfin_approve vs bizfin_approve_skip routing) and CompliancePanel (drives
// the "REQUIRED · ≥ ₹10L" chip + copy).
export const MIN_COMPLIANCE_VOLUME = 1_000_000;

export type BizFinApproveAction = "bizfin_approve" | "bizfin_approve_skip";

// True when the vendor's expected business volume meets the compliance-gate
// threshold. Only a numeric, finite value at or above the threshold triggers
// the gate — any other input (undefined, empty string, non-numeric string,
// NaN) does NOT trigger, deferring the decision to bizFinApproveAction's
// fail-closed path when businessDetailsResolved is false.
export function needsCompliance(
  businessDetails: Record<string, unknown> | undefined,
): boolean {
  const raw = businessDetails?.expected_business_volume;
  const vol = typeof raw === "number" ? raw : Number(raw ?? NaN);
  return Number.isFinite(vol) && vol >= MIN_COMPLIANCE_VOLUME;
}

// Route BizFin's Approve to Compliance-inclusive vs Compliance-skipping.
// Fail-closed contract: if `businessDetailsResolved` is false (fetch errored
// or step-1 form-id missing), OR the volume input is a non-numeric string,
// OR the value is missing, we route through Compliance regardless. Only a
// resolved, numeric, below-threshold value skips Compliance.
export function bizFinApproveAction(
  businessDetails: Record<string, unknown> | undefined,
  businessDetailsResolved: boolean,
): BizFinApproveAction {
  if (!businessDetailsResolved) return "bizfin_approve";

  const raw = businessDetails?.expected_business_volume;
  // Explicit fail-closed for non-numeric / missing input even when the fetch
  // "succeeded" (e.g. field is optional and empty). Number(undefined) === NaN
  // and Number("12,00,000") === NaN — both must route through Compliance,
  // not skip it (bug flagged in PR #165 r2).
  if (raw === undefined || raw === null || raw === "") return "bizfin_approve";
  const vol = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(vol)) return "bizfin_approve";

  return vol >= MIN_COMPLIANCE_VOLUME ? "bizfin_approve" : "bizfin_approve_skip";
}
